import type { ManufacturingCase } from '@infinitform/platform-core';

/**
 * Pure computed helper: given a project (or its `manufacturingMethods[]`) and
 * the current `activeMethodId` pointer, return the currently-active
 * ManufacturingCase — or undefined if the project has no manufacturing cases.
 *
 * Rules:
 *   - No cases (legacy pre-migration, SoftJaws, Mold)     → undefined
 *   - `activeMethodId` matches a case                     → that case
 *   - `activeMethodId` null or doesn't match              → first case
 *
 * Extracted from SessionService to keep the logic testable without the
 * service's heavy DI graph.
 */
export function deriveActiveCase(
  cases: ManufacturingCase[] | undefined,
  activeMethodId: string | null,
): ManufacturingCase | undefined {
  if (!cases || cases.length === 0) return undefined;
  if (activeMethodId) {
    const found = cases.find((c) => c.methodId === activeMethodId);
    if (found) return found;
  }
  return cases[0];
}

/**
 * The view object exposed to non-signal consumers as a stable shape.
 * Every field is optional — undefined for methods that don't use it
 * (e.g. draw direction is undefined on a Milling case).
 */
export interface ActiveCaseView {
  methodId: string | null;
  method: string | undefined;
  millingVector: ManufacturingCase['millingVector'];
  drawDirectionVector: ManufacturingCase['drawDirectionVector'];
  generatePrismatic: boolean | undefined;
  costModel: ManufacturingCase['costModel'];
  minimumToolSize: number | undefined;
  minimumToolSizeUnit: 'mm' | 'in' | undefined;
  ribsDensity: 'Low' | 'Medium' | undefined;
  wallThickness: number | 'auto' | undefined;
  bottomCutFrac: number | undefined;
  skeletonMethod: 'Skeleton3D' | 'Skeleton2D' | undefined;
  maxMillingDepth: ManufacturingCase['maxMillingDepth'];
  thinWeb: ManufacturingCase['thinWeb'];
  castingParametric: boolean | undefined;
}

/**
 * Pure projection of a ManufacturingCase into the flat `ActiveCaseView` shape.
 * Returns an "empty" view when `activeCase` is undefined so template consumers
 * can always read fields without null-checking first.
 */
export function deriveActiveView(activeCase: ManufacturingCase | undefined): ActiveCaseView {
  return {
    methodId: activeCase?.methodId ?? null,
    method: activeCase?.method,
    millingVector: activeCase?.millingVector,
    drawDirectionVector: activeCase?.drawDirectionVector,
    generatePrismatic: activeCase?.generatePrismatic,
    costModel: activeCase?.costModel,
    minimumToolSize: activeCase?.minimumToolSize,
    minimumToolSizeUnit: activeCase?.minimumToolSizeUnit,
    ribsDensity: activeCase?.ribsDensity,
    wallThickness: activeCase?.wallThickness,
    bottomCutFrac: activeCase?.bottomCutFrac,
    skeletonMethod: activeCase?.skeletonMethod,
    maxMillingDepth: activeCase?.maxMillingDepth,
    thinWeb: activeCase?.thinWeb,
    castingParametric: activeCase?.castingParametric,
  };
}
